import React from 'react';
import {KeyValueInformation} from "../AssetNameplate";
import {useQuery} from "@apollo/client";
import {AssetSpecifications as AssetSpecificationsQuery} from "../../graphql/queries/assetSpecifications";
import {Box, Stack} from "@mui/material";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {Card, Typography, useTheme} from "@mui/joy";
import {SpecificationsDataSet} from "../../types";
import {operatingFrequencyLabel, contractTypeLabel, acousticEnclosureLabel} from "../../utils/iotConnectionLabels";
import moment from "moment";
import {MapContainer, TileLayer, Marker} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

interface Props {
    assetLocationId: string;
}

const createPrimaryMarkerIcon = (color: string) => {
    const svgIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
            <path fill="${color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
    `;
    return L.divIcon({
        html: svgIcon,
        className: '',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
    });
};

const isAddressEmpty = (addr: any) =>
    !addr?.street && !addr?.city && !addr?.postalCode;

const formatDisplayValue = (value: any, unit?: string | null): string | undefined => {
    if (value === null || value === undefined) return undefined;
    let formatted: string;
    if (Array.isArray(value)) {
        formatted = value.join(', ');
    } else if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) { formatted = parsed.join(', '); }
                else { formatted = value; }
            } catch { formatted = value; }
        } else {
            formatted = value;
        }
    } else {
        formatted = String(value);
    }
    return unit ? `${formatted} ${unit}` : formatted;
};

const DataSection = ({ title, rows, truncateKey }: SpecificationsDataSet & { truncateKey?: boolean }) => {
    return (
        <Card
            variant="outlined"
            sx={{ borderRadius: '4px', overflow: 'hidden', '--Card-padding': '0px', gap: 0, backgroundColor: '#fff' }}
        >
            <Box sx={{
                px: 2,
                py: '10px',
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}>
                <Typography
                    level="body-xs"
                    fontWeight="lg"
                    sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: '0.6px' }}
                >
                    {title}
                </Typography>
            </Box>
            <Stack sx={{ px: 2, py: 1.5, gap: '2px' }}>
                {rows.map(([label, value], index) => (
                    <KeyValueInformation
                        key={index}
                        title={label}
                        value={value}
                        keySx={{ maxWidth: '400px', width: '50%' }}
                        valueSx={{ width: '50%'}}
                        truncateKey={truncateKey}
                    />
                ))}
            </Stack>
        </Card>
    );
}

interface LocationAddress {
    title?: string | null;
    street?: string | null;
    houseNumber?: string | null;
    additionalInformation?: string | null;
    postalCode?: string | null;
    city?: string | null;
    stateOrRegion?: string | null;
    country?: { name: string } | null;
    geoLocation?: { latitude: number; longitude: number } | null;
}

const LocationSection = ({ title, address }: { title: string; address: LocationAddress }) => {
    const theme = useTheme();
    // const adrGeoLocation = {
    //     "latitude": 52.771161,
    //     "longitude": 7.275344,
    //     "__typename": "GeoLocation"
    // }
    const hasGeoLocation = address.geoLocation?.latitude && address.geoLocation?.longitude;
    const primaryMarkerIcon = createPrimaryMarkerIcon(theme.palette.primary[500]);

    return (
        <Card
            variant="outlined"
            sx={{ borderRadius: '4px', overflow: 'hidden', '--Card-padding': '0px', gap: 0, backgroundColor: '#fff' }}
        >
            <Box sx={{
                px: 2,
                py: '10px',
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}>
                <Typography
                    level="body-xs"
                    fontWeight="lg"
                    sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: '0.6px' }}
                >
                    {title}
                </Typography>
            </Box>
            <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 0 }}>
                <Box sx={{ maxWidth: '400px', width: '50%', flexShrink: 0 }}>
                    {address.title && (
                        <Typography level="body-sm" fontWeight="lg" sx={{ color: "#333", mb: 1 }}>
                            {address.title}
                        </Typography>
                    )}
                    {(address.street || address.houseNumber) && (
                        <Typography level="body-sm">
                            {[address.street, address.houseNumber].filter(Boolean).join(' ')}
                        </Typography>
                    )}
                    {address.additionalInformation && (
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            {address.additionalInformation}
                        </Typography>
                    )}
                    {(address.postalCode || address.city) && (
                        <Typography level="body-sm">
                            {[address.postalCode, address.city].filter(Boolean).join(' ')}
                        </Typography>
                    )}
                    {address.stateOrRegion && (
                        <Typography level="body-sm">
                            {address.stateOrRegion}
                        </Typography>
                    )}
                    {address.country?.name && (
                        <Typography level="body-sm">
                            {address.country.name}
                        </Typography>
                    )}
                </Box>
                {hasGeoLocation && (
                    <Box sx={{
                        width: '45%',
                        maxWidth: '480px',
                        height: 150,
                        flexShrink: 0,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}>
                        <MapContainer
                            key={`${address.geoLocation!.latitude}-${address.geoLocation!.longitude}`}
                            center={[address.geoLocation!.latitude, address.geoLocation!.longitude]}
                            zoom={9}
                            scrollWheelZoom={true}
                            dragging={true}
                            zoomControl={false}
                            attributionControl={false}
                            style={{ width: '100%', height: '100%' }}
                        >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <Marker
                                position={[address.geoLocation!.latitude, address.geoLocation!.longitude]}
                                icon={primaryMarkerIcon}
                            />
                        </MapContainer>
                    </Box>
                )}
            </Box>
        </Card>
    );
}

const AssetSpecifications = ({assetLocationId}: Props) => {
    const {t} = useTranslation();
    // no-cache: Attribute/PropertyDefinition share __typename:id across
    // characteristicAttributeList, installedBaseNode.attributeList, and
    // assetLocation.attributeList; merging in the normalized cache strips
    // the `property` reference from entries whose ids collide.
    const assetSpecificationsQuery = useQuery(AssetSpecificationsQuery, {
        variables: {
            id: assetLocationId
        },
        fetchPolicy: 'no-cache'
    })

    const assetLocationData = _.get(assetSpecificationsQuery, 'data.assetLocation')
    const assetData = _.get(assetLocationData, 'asset')

    const rawDate = _.get(assetData, 'dateOfManufacture');
    const productionDate = rawDate ? moment(rawDate).format('DD.MM.YYYY') : undefined;

    const primaryAddress = _.get(assetLocationData, 'address');
    const ownerAddress = _.get(assetLocationData, 'owner.address');
    const locationAddress: LocationAddress | null = !isAddressEmpty(primaryAddress) ? primaryAddress
        : !isAddressEmpty(ownerAddress) ? ownerAddress
        : null;

    const formatEnumValue = (value: string): string =>
        /^[A-Z][A-Z0-9_]*$/.test(value) ? _.startCase(value.toLowerCase()) : value;

    const installedBaseAttributes = _.get(assetLocationData, 'installedBaseNode.attributeList.attributes', []);
    const installedBaseAttributeRows: [string, string][] = installedBaseAttributes.map((attr: any) => [
        attr.property?.preferredName || _.startCase(attr.title),
        attr.value ? formatEnumValue(attr.value) : attr.value
    ]);

    const characteristicAttributes: any[] = _.get(assetData, 'product.characteristicAttributeList.attributes', [])
        .filter((attr: any) => attr?.property);

    const OTHER_SUBCATEGORY_KEY = '__other__';
    const groupedCharacteristicAttributes = _.groupBy(
        characteristicAttributes,
        (attr: any) => attr.property.propertyDefinitionSubCategory || OTHER_SUBCATEGORY_KEY
    );
    const sortedSubCategoryKeys = Object.keys(groupedCharacteristicAttributes)
        .filter(k => k !== OTHER_SUBCATEGORY_KEY)
        .sort((a, b) => a.localeCompare(b));
    if (groupedCharacteristicAttributes[OTHER_SUBCATEGORY_KEY]) {
        sortedSubCategoryKeys.push(OTHER_SUBCATEGORY_KEY);
    }

    const assetLocationAttributes = _.get(assetLocationData, 'attributeList.attributes', []);
    const assetLocationAttributeRows: [string, string][] = assetLocationAttributes
        .filter((attr: any) => attr?.property)
        .map((attr: any) => [
            attr.property.preferredName,
            formatDisplayValue(attr.displayValue, attr.property?.unit?.label || attr.unit) ?? ''
        ]);

    const generalSection: SpecificationsDataSet = {
        id: 'general',
        title: t("General"),
        rows: [
            [t("Production Date"), productionDate],
            [t("Company"), _.get(assetLocationData, 'owner.name')],
            [t("Category"), _.get(assetData, 'product.productCategory.name')],
        ]
    };

    const iotConnection = _.get(assetLocationData, 'iotConnection');
    const oilCategory = _.get(iotConnection, 'oilCategory');
    const frequencyLabel = operatingFrequencyLabel(_.get(iotConnection, 'operatingFrequency'));
    const enclosureLabel = acousticEnclosureLabel(_.get(iotConnection, 'acousticEnclosure'));
    const typeLabel = contractTypeLabel(_.get(assetLocationData, 'owner.contract.contractType'));

    // IoT-connected assets only — non-IoT assets see no change.
    const iotConfigurationSection: SpecificationsDataSet | null = _.get(iotConnection, 'id') ? {
        id: 'iotConfiguration',
        title: t("IoT Configuration"),
        rows: [
            [t("IMEI"), _.get(iotConnection, 'gateway.imei')],
            [t("Oil category"), oilCategory ? formatEnumValue(oilCategory) : undefined],
            [t("Oil type"), _.get(iotConnection, 'oilType')],
            [t("Operating frequency"), frequencyLabel ? t(frequencyLabel) : undefined],
            [t("Acoustic enclosure"), enclosureLabel ? t(enclosureLabel) : undefined],
            [t("Contract type"), typeLabel ? t(typeLabel) : undefined],
        ]
    } : null;

    type TopSection =
        | { kind: 'data'; data: SpecificationsDataSet }
        | { kind: 'location'; address: LocationAddress };

    const topSections: TopSection[] = [
        { kind: 'data', data: generalSection },
        ...(iotConfigurationSection ? [{ kind: 'data' as const, data: iotConfigurationSection }] : []),
        ...(locationAddress ? [{ kind: 'location' as const, address: locationAddress }] : []),
        ...(installedBaseAttributeRows.length > 0 ? [{
            kind: 'data' as const,
            data: { id: 'installedBaseAttributes', title: t("Installed Base Attributes"), rows: installedBaseAttributeRows }
        }] : []),
        ...(assetLocationAttributeRows.length > 0 ? [{
            kind: 'data' as const,
            data: { id: 'assetAttributes', title: t("Asset Attributes"), rows: assetLocationAttributeRows }
        }] : [])
    ];

    const productSpecSections: SpecificationsDataSet[] = sortedSubCategoryKeys.map(key => ({
        id: `characteristicAttributes-${key}`,
        title: key === OTHER_SUBCATEGORY_KEY ? t("Other attributes") : key,
        rows: groupedCharacteristicAttributes[key].map((attr: any) => [
            attr.property.preferredName,
            formatDisplayValue(attr.displayValue, attr.property?.unit?.label || attr.unit) ?? ''
        ]) as [string, string][]
    }));

    const splitInTwo = <T,>(arr: T[]): [T[], T[]] => {
        const mid = Math.ceil(arr.length / 2);
        return [arr.slice(0, mid), arr.slice(mid)];
    };
    const [topLeft, topRight] = splitInTwo(topSections);
    const [productLeft, productRight] = splitInTwo(productSpecSections);

    const renderTopSection = (s: TopSection) =>
        s.kind === 'data'
            ? <DataSection key={s.data.id} {...s.data} truncateKey />
            : <LocationSection key="address" title={t("Address")} address={s.address} />;

    const twoColumnSx = {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        '@media (min-width: 1350px)': {
            flexDirection: 'row',
            alignItems: 'flex-start',
        }
    } as const;

    return (
        <Stack spacing={3} sx={{ pt: "8px" }}>
            <Box sx={twoColumnSx}>
                <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                    {topLeft.map(renderTopSection)}
                </Stack>
                <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                    {topRight.map(renderTopSection)}
                </Stack>
            </Box>

            {productSpecSections.length > 0 && (
                <Stack spacing={2}>
                    <Typography level="title-lg" fontWeight="lg">
                        {t("Product Specifications")}
                    </Typography>
                    <Box sx={twoColumnSx}>
                        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                            {productLeft.map(section => (
                                <DataSection key={section.id} {...section} truncateKey />
                            ))}
                        </Stack>
                        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                            {productRight.map(section => (
                                <DataSection key={section.id} {...section} truncateKey />
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            )}
        </Stack>
    );
}

export default AssetSpecifications;
